<template>
    <div class="container-fluid p-3">
        <h1 class="fs-5 fw-bolder mb-3">{{ $t('taskDetail.simsSignals') }}</h1>

        <div class="row g-3">
            <div class="col-12 col-sm-6 col-md-4">
                <select id="simsAccountsSelect" v-model="selectedSimsAccount" class="form-select bg-light"
                    @change="onFiltersChange">
                    <option value="">{{ $t('taskDetail.allOption') }}</option>
                    <option
                        v-for="account in simsAccounts"
                        :key="account.sims_account"
                        :value="account.sims_account"
                    >
                        {{ account.sims_account }}
                    </option>
                </select>
            </div>
            <div class="col-12 col-sm-6 col-md-4">
                <VueDatePicker
                    auto-apply
                    format="dd.MM.yyyy"
                    v-model="dateRange"
                    :locale="locale"
                    :cancel-text="$t('common.cancel')"
                    :select-text="$t('common.select')"
                    :range="{ }"
                    :clearable="true"
                    :closeOnScroll="true"
                    @update:modelValue="onFiltersChange"
                />
            </div>
            <div class="col-12 col-sm-12 col-md-4 d-flex align-items-center">
                <div class="form-check form-switch ms-4">
                    <input class="form-check-input" type="checkbox"
                        id="autoUpdateSwitch"
                        v-model="autoUpdate"
                        @change="updateQuery"
                        :disabled="!simsAccounts.length" />
                    <label class="form-check-label" for="autoUpdateSwitch">
                        {{ $t('taskDetail.autoUpdate') }}
                    </label>
                </div>
            </div>
        </div>

        <div class="table-responsive">
            <table class="table mt-3 small">
                <thead>
                    <tr>
                        <th scope="col">{{ $t('taskDetail.sims.time') }}</th>
                        <th scope="col">{{ $t('taskDetail.sims.account') }}</th>
                        <th scope="col">{{ $t('taskDetail.sims.signal') }}</th>
                        <th scope="col">{{ $t('taskDetail.sims.type') }}</th>
                        <th scope="col">{{ $t('taskDetail.sims.description') }}</th>
                        <th scope="col">{{ $t('taskDetail.sims.disposition') }}</th>
                        <th scope="col">{{ $t('taskDetail.sims.comment') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="apiSignalsError">
                        <td colspan="7" class="text-center">{{ $t('taskDetail.noConnection') }}</td>
                    </tr>
                    <tr v-else-if="!apiSignalsError && signals.length === 0">
                        <td colspan="7" class="text-center">{{ $t('taskDetail.noSignals') }}</td>
                    </tr>
                    <tr v-else v-for="signal in signals" :key="signal.EventSummaryId">
                        <td>{{ signal.received }}</td>
                        <td>{{ signal.account }}</td>
                        <td>{{ signal.zone }}</td>
                        <td>{{ signal.eventType }}</td>
                        <td>{{ signal.description }}</td>
                        <td>{{ signal.disposition }}</td>
                        <td>{{ signal.comment }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDate } from '@/composables/useDate';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    name: 'SimsSignalsView',
    components: {
        VueDatePicker
    },
    setup() {
        const route = useRoute();
        const { locale } = useI18n();
        const { formatDate } = useDate();

        return { taskId: route.params.taskId, locale, formatDate };
    },
    data() {
        return {
            simsAccounts: [],
            signals: [],
            selectedSimsAccount: '',
            dateRange: null,
            autoUpdate: false,
            autoUpdateIntervalId: null,
            apiSignalsError: false,
        };
    },
    watch: {
        autoUpdate(newValue) {
            if (newValue) {
                this.startAutoUpdate();
            } else {
                this.stopAutoUpdate();
            }
        }
    },
    created() {
        // Seed the filters from the query params the task page handed over.
        const query = this.$route.query;

        if (typeof query.sims_id === 'string') {
            this.selectedSimsAccount = query.sims_id;
        }

        const dateFrom = this.parseQueryDate(query.date_from);
        const dateTo = this.parseQueryDate(query.date_to);
        if (dateFrom && dateTo) {
            this.dateRange = [dateFrom, dateTo];
        }

        this.autoUpdate = query.auto === '1';
    },
    mounted() {
        this.fetchSimsAccounts();
    },
    beforeUnmount() {
        this.stopAutoUpdate();
    },
    methods: {
        parseQueryDate(value) {
            if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
            const date = new Date(`${value}T00:00:00`);
            return isNaN(date.getTime()) ? null : date;
        },
        onFiltersChange() {
            this.updateQuery();
            this.fetchSignals();
        },
        updateQuery() {
            // Keep the filters in the URL so a refresh (or a shared link) keeps them.
            const query = {};

            if (this.selectedSimsAccount) {
                query.sims_id = this.selectedSimsAccount;
            }
            if (Array.isArray(this.dateRange) && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
                query.date_from = this.formatDate(this.dateRange[0]);
                query.date_to = this.formatDate(this.dateRange[1]);
            }
            if (this.autoUpdate) {
                query.auto = '1';
            }

            this.$router.replace({ query }).catch(() => {});
        },
        async fetchSimsAccounts() {
            try {
                const response = await axios.get(`tasks/${this.taskId}/sims-accounts`);
                const accounts = response.data?.data;
                this.simsAccounts = Array.isArray(accounts) ? accounts : [];
            } catch (error) {
                console.error('Error fetching SIMs accounts:', error);
                this.simsAccounts = [];
                this.apiSignalsError = true;
                return;
            }

            this.fetchSignals();
        },
        async fetchSignals() {
            // The task route resolves every code authorized for the task on its own,
            // so sims_id is only sent to narrow that set down to one account.
            const params = {};
            if (this.selectedSimsAccount) {
                params.sims_id = this.selectedSimsAccount;
            }

            // Build date_from and date_to from dateRange
            if (Array.isArray(this.dateRange) && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
                params.date_from = this.formatDate(this.dateRange[0]);
                params.date_to = this.formatDate(this.dateRange[1]);
            }

            try {
                this.apiSignalsError = false;
                const response = await axios.get(`tasks/${this.taskId}/sims-signals`, { params });

                this.signals = response.data.data;
            } catch (error) {
                // 404 is the API's "no signal" answer, not a broken connection.
                if (error?.response?.status !== 404) {
                    console.error('Error fetching signals:', error);
                    this.apiSignalsError = true;
                }
                this.signals = [];
            }
        },
        startAutoUpdate() {
            // Clear any existing interval just in case
            this.stopAutoUpdate();

            // Fetch immediately once
            this.fetchSignals();

            // Then set up a 10-second interval
            this.autoUpdateIntervalId = setInterval(() => {
                this.fetchSignals();
            }, 10000);
        },
        stopAutoUpdate() {
            if (this.autoUpdateIntervalId) {
                clearInterval(this.autoUpdateIntervalId);
                this.autoUpdateIntervalId = null;
            }
        },
    }
};
</script>
